/**
 * Request Detail Drawer — W3-A · B7.
 *
 * Shared drawer used by MyRequests + Approvals Queue to render the
 * full state of a workflow instance: the request payload, workflow
 * metadata, and the immutable history timeline.
 *
 * The drawer takes an ``instance_id`` and re-fetches instance + history
 * on open so writes done elsewhere (withdraw, approve, reject) are
 * always reflected on next open without stale caches.
 */
import { useEffect, useState, useCallback } from "react";
import {
    Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "../../../components/ui/sheet";
import { Button } from "../../../components/ui/button";
import { api, formatApiError } from "../../../lib/api";
import { toast } from "sonner";
import { WorkflowStatusBadge, EVENT_LABEL, formatDT } from "../workflowUi";


export default function RequestDetailDrawer({
    open,
    onClose,
    instanceId,
    canWithdraw = false,
    onWithdrawn,
    canApprove = false,
    onApproveClicked,
    onRejectClicked,
}) {
    const [instance, setInstance] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [withdrawing, setWithdrawing] = useState(false);

    const load = useCallback(async () => {
        if (!instanceId) return;
        setLoading(true);
        try {
            const [i, h] = await Promise.all([
                api.get(`/workflows/instances/${instanceId}`),
                api.get(`/workflows/instances/${instanceId}/history`),
            ]);
            setInstance(i.data);
            setHistory(h.data.items || []);
        } catch (err) {
            toast.error(formatApiError(err) || "Failed to load request");
        } finally {
            setLoading(false);
        }
    }, [instanceId]);

    useEffect(() => {
        if (open) load();
    }, [open, load]);

    async function handleWithdraw() {
        if (!instance) return;
        // Basic confirmation — the destructive intent is clearly
        // labelled on the trigger button.
        if (!window.confirm(
            "Withdraw this Project Request?  It will not be reviewed further.",
        )) {
            return;
        }
        setWithdrawing(true);
        try {
            await api.post(
                `/workflows/instances/${instance.workflow_instance_id}/withdraw`,
                { confirm: true, note: "withdrawn by initiator" },
                {
                    headers: {
                        "Idempotency-Key": `wd-${instance.workflow_instance_id}`,
                    },
                },
            );
            toast.success("Request withdrawn.");
            onWithdrawn?.();
        } catch (err) {
            toast.error(formatApiError(err) || "Withdrawal failed");
        } finally {
            setWithdrawing(false);
        }
    }

    const payload = instance?.payload || {};
    const isPending = instance?.status?.startsWith("pending");

    return (
        <Sheet open={open} onOpenChange={(o) => !o && onClose?.()}>
            <SheetContent
                side="right"
                className="sm:max-w-xl overflow-y-auto"
                data-testid="request-detail-drawer"
            >
                <SheetHeader>
                    <SheetTitle>Project Request</SheetTitle>
                    <SheetDescription>
                        Reference · {instance ? instance.workflow_instance_id : "…"}
                    </SheetDescription>
                </SheetHeader>

                {loading ? (
                    <div className="text-sm text-slate-500 py-6">Loading…</div>
                ) : null}

                {instance ? (
                    <div className="space-y-5 py-4">
                        <div className="flex items-center gap-2">
                            <WorkflowStatusBadge status={instance.status} />
                            {instance.override_at ? (
                                <span
                                    className="text-xs text-purple-700"
                                    data-testid="wf-emergency-marker"
                                >
                                    Created via Emergency Project Creation
                                </span>
                            ) : null}
                        </div>

                        <div className="border rounded-md p-3 bg-slate-50 space-y-1 text-sm">
                            <div className="font-semibold text-slate-800 mb-1">
                                Project details
                            </div>
                            <Row label="Project name" value={payload.name} />
                            <Row label="Project type" value={payload.project_type} />
                            {payload.client_id ? (
                                <Row label="Client" value={payload.client_id} />
                            ) : null}
                            {payload.prospect_id ? (
                                <Row label="Prospect" value={payload.prospect_id} />
                            ) : null}
                            {payload.organisational_context ? (
                                <>
                                    <Row
                                        label="Business Unit"
                                        value={
                                            payload.organisational_context.business_unit_id
                                            || payload.organisational_context.business_unit
                                        }
                                    />
                                    <Row
                                        label="Cost Centre"
                                        value={
                                            payload.organisational_context.cost_centre_id
                                            || payload.organisational_context.cost_centre
                                        }
                                    />
                                    <Row
                                        label="Initiative"
                                        value={payload.organisational_context.initiative}
                                    />
                                    {payload.context_source === "approver" ? (
                                        <div
                                            className="mt-1 text-xs text-sky-800 bg-sky-50 border border-sky-200 rounded px-2 py-1"
                                            data-testid="wf-context-source-approver"
                                        >
                                            Business Unit + Cost Centre assigned
                                            by the approver at approval time.
                                        </div>
                                    ) : null}
                                </>
                            ) : payload.project_type === "internal" && (
                                payload.context_pending
                                || payload.context_source === "pending"
                            ) ? (
                                <div
                                    className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1"
                                    data-testid="wf-context-pending-note"
                                >
                                    Business Unit and Cost Centre — to be
                                    assigned by the approver during approval.
                                </div>
                            ) : null}
                            {payload.priority ? (
                                <Row label="Priority" value={payload.priority} />
                            ) : null}
                        </div>

                        <div className="border rounded-md p-3 bg-slate-50 space-y-1 text-sm">
                            <div className="font-semibold text-slate-800 mb-1">
                                Request status
                            </div>
                            <Row
                                label="Submitted"
                                value={formatDT(instance.created_at)}
                            />
                            <Row
                                label="Submitted by"
                                value={instance.initiator_user_id}
                            />
                            {instance.approved_by_user_id ? (
                                <Row
                                    label="Approved by"
                                    value={`${instance.approved_by_user_id} · ${formatDT(instance.approved_at)}`}
                                />
                            ) : null}
                            {instance.rejected_by_user_id ? (
                                <Row
                                    label="Rejected by"
                                    value={`${instance.rejected_by_user_id} · ${formatDT(instance.rejected_at)}`}
                                />
                            ) : null}
                            {instance.rejection_reason ? (
                                <div
                                    className="mt-2 px-2 py-1 rounded bg-red-50 border border-red-200 text-red-800"
                                    data-testid="wf-rejection-reason"
                                >
                                    <div className="font-medium">Rejection reason</div>
                                    <div>{instance.rejection_reason}</div>
                                </div>
                            ) : null}
                            {instance.withdrawn_at ? (
                                <Row
                                    label="Withdrawn"
                                    value={formatDT(instance.withdrawn_at)}
                                />
                            ) : null}
                            {instance.target_id ? (
                                <Row
                                    label="Active Project"
                                    value={instance.target_id}
                                />
                            ) : null}
                        </div>

                        <div className="border rounded-md p-3 space-y-2 text-sm">
                            <div className="font-semibold text-slate-800 mb-1">
                                History timeline
                            </div>
                            <ol
                                className="space-y-1.5 relative border-l pl-3 border-slate-200"
                                data-testid="wf-history-timeline"
                            >
                                {history.length === 0 ? (
                                    <li className="text-slate-500">No events yet.</li>
                                ) : null}
                                {history.map((ev, i) => (
                                    <li key={i} className="text-slate-700 pl-2">
                                        <span className="font-medium">
                                            {EVENT_LABEL[ev.event] || ev.event}
                                        </span>
                                        <span className="text-slate-500 text-xs ml-2">
                                            {formatDT(ev.at)}
                                        </span>
                                        {ev.actor_user_id ? (
                                            <span className="text-slate-500 text-xs ml-2">
                                                by {ev.actor_user_id}
                                            </span>
                                        ) : null}
                                    </li>
                                ))}
                            </ol>
                        </div>

                        <div className="flex flex-wrap gap-2 justify-end">
                            {canWithdraw && isPending ? (
                                <Button
                                    variant="outline"
                                    onClick={handleWithdraw}
                                    disabled={withdrawing}
                                    data-testid="wf-withdraw-btn"
                                >
                                    {withdrawing ? "Withdrawing…" : "Withdraw Request"}
                                </Button>
                            ) : null}
                            {canApprove && isPending ? (
                                <>
                                    <Button
                                        variant="outline"
                                        className="border-red-500 text-red-700 hover:bg-red-50"
                                        onClick={() => onRejectClicked?.(instance)}
                                        data-testid="wf-reject-btn"
                                    >
                                        Reject Request
                                    </Button>
                                    <Button
                                        onClick={() => onApproveClicked?.(instance)}
                                        data-testid="wf-approve-btn"
                                    >
                                        Approve Request
                                    </Button>
                                </>
                            ) : null}
                        </div>
                    </div>
                ) : null}
            </SheetContent>
        </Sheet>
    );
}


function Row({ label, value }) {
    return (
        <div className="flex justify-between gap-4">
            <span className="text-slate-500">{label}</span>
            <span className="text-slate-800 text-right break-all">{value || "—"}</span>
        </div>
    );
}
